<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type {
  CreateModuleUserRequest,
  ModuleMetadata,
  ModuleUser,
  PageResult,
  UpdateModuleUserRequest,
  UpdateModuleUserStatusRequest
} from '../../types'

const props = defineProps<{
  createApiClient: () => ApiClient
  modules: ModuleMetadata[]
  canCreate: boolean
  canUpdate: boolean
}>()

const rawUsers = ref<ModuleUser[]>([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const loading = ref(false)

const moduleCode = ref(props.modules[0]?.moduleCode ?? '')
const showCreateForm = ref(false)
const filters = ref({
  keyword: '',
  status: 'ALL' as 'ALL' | ModuleUser['status']
})
const drafts = ref<Record<number, UpdateModuleUserRequest>>({})
const form = ref<CreateModuleUserRequest>({
  moduleCode: moduleCode.value,
  username: 'member001',
  nickname: '模块业务用户',
  mobile: '18800030001',
  email: 'member001@example.com',
  externalId: 'member001',
  source: 'saas'
})

const singleModule = computed(() => props.modules.length === 1)
const selectedModuleName = computed(() => moduleName(moduleCode.value))

const moduleOptions = computed(() => {
  return [
    { label: '全部模块', value: '' },
    ...props.modules.map((m) => ({ label: m.moduleName, value: m.moduleCode }))
  ]
})

const statusOptions = [
  { label: '全部状态', value: 'ALL' },
  { label: '启用', value: 'ACTIVE' },
  { label: '停用', value: 'SUSPENDED' }
]

const formModuleOptions = computed(() => {
  return props.modules.map((m) => ({ label: m.moduleName, value: m.moduleCode }))
})

async function fetchUsers() {
  loading.value = true
  try {
    const api = props.createApiClient()
    const params = new URLSearchParams({
      pageNo: String(page.value),
      pageSize: String(pageSize.value)
    })
    if (moduleCode.value) params.set('moduleCode', moduleCode.value)
    if (filters.value.keyword.trim()) params.set('keyword', filters.value.keyword.trim())
    if (filters.value.status !== 'ALL') params.set('status', filters.value.status)
    const res = await api.get<PageResult<ModuleUser>>(`/api/saas/system/module-users?${params.toString()}`)
    rawUsers.value = res.records
    total.value = res.total
    drafts.value = Object.fromEntries(res.records.map((user) => [user.id, draftFor(user)]))
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void fetchUsers()
})

watch([page, pageSize], () => {
  void fetchUsers()
})

watch([() => moduleCode.value, () => filters.value.keyword, () => filters.value.status], () => {
  page.value = 1
  void fetchUsers()
})

function moduleName(value: string) {
  return props.modules.find((module) => module.moduleCode === value)?.moduleName ?? value
}

function statusText(status: ModuleUser['status']) {
  return status === 'ACTIVE' ? '启用' : '停用'
}

function draftFor(user: ModuleUser): UpdateModuleUserRequest {
  return drafts.value[user.id] ?? {
    nickname: user.nickname,
    mobile: user.mobile,
    email: user.email,
    externalId: user.externalId,
    source: user.source
  }
}

function resetFilters() {
  filters.value = {
    keyword: '',
    status: 'ALL'
  }
}

async function submitCreate() {
  if (!moduleCode.value && !form.value.moduleCode) return
  loading.value = true
  try {
    const api = props.createApiClient()
    await api.post<ModuleUser>('/api/saas/system/module-users', {
      ...form.value,
      moduleCode: moduleCode.value || form.value.moduleCode,
      source: 'saas'
    })
    showCreateForm.value = false
    await fetchUsers()
  } finally {
    loading.value = false
  }
}

async function submitUpdate(user: ModuleUser) {
  loading.value = true
  try {
    const api = props.createApiClient()
    await api.put<ModuleUser>(`/api/saas/system/module-users/${user.id}`, draftFor(user))
    await fetchUsers()
  } finally {
    loading.value = false
  }
}

async function submitUpdateStatus(userId: number, status: ModuleUser['status']) {
  loading.value = true
  try {
    const api = props.createApiClient()
    await api.put<ModuleUser>(`/api/saas/system/module-users/${userId}/status`, { status })
    await fetchUsers()
  } finally {
    loading.value = false
  }
}

watch(
  () => props.modules,
  (modules) => {
    if (!modules.some((module) => module.moduleCode === moduleCode.value)) {
      moduleCode.value = modules[0]?.moduleCode ?? ''
    }
  }
)

watch(moduleCode, (value) => {
  form.value.moduleCode = value
})

const columns: TableColumnType<ModuleUser>[] = [
  { key: 'user', title: '用户', minWidth: 200 },
  { key: 'nickname', title: '昵称', minWidth: 150 },
  { key: 'mobile', title: '手机号', minWidth: 150 },
  { key: 'email', title: '邮箱', minWidth: 150 },
  { key: 'status', title: '状态', width: 100 },
  { key: 'actions', title: '操作', width: 220, align: 'right', fixed: 'right' }
]
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Module User</p>
        <h2>模块用户</h2>
        <small>{{ selectedModuleName }} · 模块业务身份，不承担客户端登录鉴权</small>
      </div>
      <span>共 {{ total }} 个模块用户</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="resetFilters">
      <RrSelect v-if="!singleModule" v-model:value="moduleCode" :options="moduleOptions" placeholder="全部模块" />
      <RrInput v-model:value.trim="filters.keyword" placeholder="账号、昵称、手机号、邮箱、外部 ID" />
      <RrSelect v-model:value="filters.status" :options="statusOptions" placeholder="全部状态" />

      <template #actions>
        <RrButton type="primary" :disabled="loading || !canCreate || !moduleCode" @click="showCreateForm = true">
          新增用户
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer v-model:open="showCreateForm" title="创建模块用户">
      <div style="margin-bottom: 24px; color: var(--rr-color-text-muted);">
        <span>{{ selectedModuleName }}</span>
      </div>
      <RrForm :disabled="loading || !canCreate" @submit.prevent="submitCreate" layout="vertical">
        <RrFormItem v-if="!singleModule" label="模块">
          <RrSelect v-model:value="moduleCode" :options="formModuleOptions" />
        </RrFormItem>
        <RrFormItem label="账号">
          <RrInput :value="form.username ?? ''" @update:value="form.username = $event" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="昵称">
          <RrInput :value="form.nickname ?? ''" @update:value="form.nickname = $event" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="手机号">
          <RrInput :value="form.mobile ?? ''" @update:value="form.mobile = $event" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="邮箱">
          <RrInput :value="form.email ?? ''" @update:value="form.email = $event" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="外部用户 ID">
          <RrInput :value="form.externalId ?? ''" @update:value="form.externalId = $event" autocomplete="off" />
        </RrFormItem>
        <RrButton type="primary" html-type="submit" :loading="loading" :disabled="!canCreate || !moduleCode" style="margin-top: 16px;">
          创建用户
        </RrButton>
      </RrForm>
    </AdminDrawer>

    <AdminDataTable
      :columns="columns"
      :data-source="rawUsers"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      row-key="id"
      :count="rawUsers.length"
      :empty="total === 0"
      empty-title="暂无匹配的模块用户"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'user'">
          <div style="display: flex; flex-direction: column;">
            <strong>{{ record.username }}</strong>
            <small style="color: var(--rr-color-text-muted)">{{ moduleName(record.moduleCode) }} · {{ record.externalId || '无外部 ID' }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'nickname'">
          <RrInput v-if="drafts[record.id]" v-model:value.trim="drafts[record.id].nickname" autocomplete="off" />
        </template>
        <template v-else-if="column.key === 'mobile'">
          <RrInput v-if="drafts[record.id]" v-model:value.trim="drafts[record.id].mobile" autocomplete="off" placeholder="手机号" />
        </template>
        <template v-else-if="column.key === 'email'">
          <RrInput v-if="drafts[record.id]" v-model:value.trim="drafts[record.id].email" autocomplete="off" placeholder="邮箱" />
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag :tone="record.status === 'ACTIVE' ? 'success' : 'neutral'" :label="statusText(record.status)" />
        </template>
        <span v-else-if="column.key === 'actions'" class="row-actions compact-actions">
          <RrButton type="default" :disabled="loading || !canUpdate" @click="submitUpdate(record)">保存</RrButton>
          <RrButton
            type="default"
            :disabled="loading || !canUpdate || record.status === 'ACTIVE'"
            @click="submitUpdateStatus(record.id, 'ACTIVE')"
          >
            启用
          </RrButton>
          <RrButton
            type="default"
            danger
            :disabled="loading || !canUpdate || record.status === 'SUSPENDED'"
            @click="submitUpdateStatus(record.id, 'SUSPENDED')"
          >
            停用
          </RrButton>
        </span>
      </template>
    </AdminDataTable>
  </section>
</template>
